import { Box, Divider, Typography } from '@mui/material';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import DialogBox from '../Dialog Box/Dialog Box';
import Drawer from '../Drawer/Drawer';
import Footer from '../Sections/Footer';
import { getEntityThemedCtaInlineStyle } from '../../utils/ctaContrast.utils';
import { useTranslation } from '../../hooks/useTranslation';

const STEPPER_INFO_TITLE_ID = 'stepper-info-dialog-title';

const NORMAL_STEP_ITEMS = [
	{
		icon: VpnKeyOutlinedIcon,
		title: 'Register',
		desc: 'Sign up onto Finvu AA, an RBI Licensed Account Aggregator using your mobile number or handle.',
	},
	{
		icon: LinkOutlinedIcon,
		title: 'Select Accounts',
		desc: 'Add & choose which bank accounts you want to share. You stay in control, only selected accounts are shared.',
	},
	{
		icon: GppGoodOutlinedIcon,
		title: 'Approve Consents',
		desc: 'Review the details shared and for how long. Approve to share your data securely.',
	},
];

function getStepItems(isConsentRenewal, t) {
	if (!isConsentRenewal) {
		return NORMAL_STEP_ITEMS;
	}

	return [
		NORMAL_STEP_ITEMS[0],
		{
			icon: DescriptionOutlinedIcon,
			title: 'Review Consent Notice',
			desc: t('login.infographic.consentRenewalStep2'),
		},
		NORMAL_STEP_ITEMS[2],
	];
}

function StepperInfoTitle({ customDetails, isDark }) {
	return (
		<Typography
			component="h2"
			id={STEPPER_INFO_TITLE_ID}
			sx={{
				fontWeight: 700,
				fontSize: '1rem',
				lineHeight: 1.4,
				mb: '1.25rem',
				fontFamily: customDetails?.fontFamily,
				color: isDark ? '#FFFFFF' : '#000000',
			}}
		>
			Sharing data securely to lenders through Account Aggregator in 3 easy steps
		</Typography>
	);
}

function StepperInfoSteps({ customDetails, isDark, isConsentRenewal, t }) {
	const stepItems = getStepItems(isConsentRenewal, t);

	return (
		<>
			{stepItems.map((step, i) => {
				const Icon = step.icon;
				return (
					<Box key={i} sx={{ mb: '1.25rem' }}>
						<Box sx={{ mb: '0.375rem' }} aria-hidden="true">
							<Icon
								sx={{ fontSize: '1.5rem', color: isDark ? '#aaa' : '#444' }}
							/>
						</Box>
						<Typography
							component="h3"
							sx={{
								fontWeight: 700,
								fontSize: '0.9375rem',
								fontFamily: customDetails?.fontFamily,
								mb: '0.25rem',
								color: isDark ? '#FFFFFF' : '#000000',
							}}
						>
							{step.title}
						</Typography>
						<Typography
							component="p"
							sx={{
								fontSize: '0.875rem',
								fontFamily: customDetails?.fontFamily,
								lineHeight: 1.5,
								color: isDark ? '#B0B0B0' : '#666',
							}}
						>
							{step.desc}
						</Typography>
					</Box>
				);
			})}
		</>
	);
}

function StepperInfoActions({
	customDetails,
	isDark,
	onClose,
	dividerMt = '1.5rem',
}) {
	return (
		<Box>
			<Divider
				sx={{
					mt: dividerMt,
					mb: '1.5rem',
					marginLeft: '-1.5rem',
					marginRight: '-1.5rem',
					width: 'calc(100% + 3rem)',
				}}
			/>
			<Box
				component="button"
				type="button"
				onClick={onClose}
				style={getEntityThemedCtaInlineStyle(customDetails, isDark, {
					width: '100%',
					padding: '10px 0',
					borderRadius: customDetails?.borderRadius || '6px',
					border: 'none',
					cursor: 'pointer',
					fontFamily: customDetails?.fontFamily,
					fontSize: '0.9375rem',
					fontWeight: 500,
					textTransform: 'capitalize',
					boxShadow: 'none',
				})}
			>
				Understood
			</Box>
		</Box>
	);
}

function StepperInfoDialog({
	open,
	onClose,
	customDetails,
	isMobile,
	isDark,
	isConsentRenewal = Boolean(global.renewConsentId),
}) {
	const { t } = useTranslation();
	const contentBackground = isDark
		? '#161616'
		: customDetails?.secondaryColor || '#fff';

	if (isMobile) {
		return (
			<Drawer
				open={open}
				setOpen={(nextOpen) => {
					if (!nextOpen) {
						onClose();
					}
				}}
				anchor="bottom"
				height="auto"
				customDetails={customDetails}
				showCloseBtn={false}
				flushBottom
				ariaLabelledBy={STEPPER_INFO_TITLE_ID}
			>
				<Box
					sx={{
						fontFamily: customDetails?.fontFamily,
						px: '1.5rem',
						pt: '1.5rem',
						pb: '1.5rem',
						backgroundColor: contentBackground,
					}}
				>
					<Box
						tabIndex={0}
						sx={{
							maxHeight: '55vh',
							overflowY: 'auto',
							outline: 'none',
						}}
					>
						<StepperInfoTitle customDetails={customDetails} isDark={isDark} />
						<StepperInfoSteps
							customDetails={customDetails}
							isDark={isDark}
							isConsentRenewal={isConsentRenewal}
							t={t}
						/>
					</Box>
					<StepperInfoActions
						customDetails={customDetails}
						isDark={isDark}
						onClose={onClose}
						dividerMt="2.5rem"
					/>
				</Box>
				<Footer customDetails={customDetails} />
			</Drawer>
		);
	}

	return (
		<DialogBox
			open={open}
			handleClose={onClose}
			boxPadding="1.5rem"
			customDetails={customDetails}
			showCloseButton
			closeButtonVariant="neutral"
			ariaLabelledBy={STEPPER_INFO_TITLE_ID}
		>
			<StepperInfoTitle customDetails={customDetails} isDark={isDark} />
			<StepperInfoSteps
				customDetails={customDetails}
				isDark={isDark}
				isConsentRenewal={isConsentRenewal}
				t={t}
			/>
			<StepperInfoActions
				customDetails={customDetails}
				isDark={isDark}
				onClose={onClose}
			/>
		</DialogBox>
	);
}

export default StepperInfoDialog;
